'use client';

// BL-2473 — pops a mention the moment it is delivered, on whatever admin page
// the person has open, in every open tab. The staff twin of the bidder's
// `CustomerToastListener`: the same shared `useToastLedger` loop (subscribe,
// show each new item once), on the person's private toast ledger at the active
// house (`staff-user:{id}:tenant:{tenantId}:toasts:ledger:{token}`), minted by
// the same call that mints the @Inbox count's topic.
//
// View opens the @Inbox deep link `/inbox?item=<noteId>` (BL-2469), which marks
// the item read on the server first and then opens the record at the note: one
// path for the email's link and the pop-up. Dismiss closes it and leaves the
// item unread.
//
// A pop-up closes on its own after 8 seconds, paused while the pointer is over
// it, keyboard focus is inside it or the tab is hidden (BL-2734, superseding
// BL-2473 AC 7). Closing on its own is Dismiss: the mention stays unread, and the
// @Inbox count keeps it. It never outlives its house: the subscription is keyed on the house's topic,
// and unmounting it (a house switch, sign-out) dismisses every pop-up it showed,
// so one house's mention is never shown, or opened, under another.
//
// Mounted in the `(dashboard)` and `(clerk)` layouts, under the realtime stack
// (which supplies the LedgerProvider) and beside a toaster.

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from '@auctogon/ui/web';
import { useToastLedger, type PublicStaffToastEntry } from '@auctogon/core/realtime';

import { useStaffTopics, useStaffTopicsKey } from './InboxCountBridge';
import MentionToast from './MentionToast';

/** The @Inbox's own route, which opens a mention by its note. */
const INBOX_PATH = '/inbox';

/**
 * Topic gate: renders nothing, and opens no subscription, until the person's
 * toast topic is minted. Hooks run before the early return.
 *
 * @returns the subscription, or nothing.
 * @sideEffect Mints the person's private topics at the active house (shared cache).
 */
export default function StaffMentionToastListener() {
  const { toastsTopic } = useStaffTopics(useStaffTopicsKey());
  if (!toastsTopic) return null;
  return <MentionToastSubscription key={toastsTopic} topic={toastsTopic} />;
}

/**
 * Subscribes to the minted topic and pops each new mention once.
 *
 * @param props.topic - the person's tokened toast ledger topic.
 * @returns null.
 * @sideEffect Subscribes to the topic; shows toasts; navigates on View;
 *   dismisses its toasts on unmount.
 */
function MentionToastSubscription({ topic }: { topic: string }) {
  const router = useRouter();
  // Every pop-up this house's subscription showed, closed when it unmounts.
  const shownIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const shown = shownIdsRef.current;
    return () => {
      for (const id of shown) toast.dismiss(id);
    };
  }, []);

  const show = useCallback(
    (item: PublicStaffToastEntry) => {
      shownIdsRef.current.add(item.id);
      const href = item.internalNoteId ? `${INBOX_PATH}?item=${encodeURIComponent(item.internalNoteId)}` : INBOX_PATH;
      toast.custom(
        (id) => (
          <MentionToast
            senderName={item.senderName}
            bodyHtml={item.bodyHtml}
            onView={() => {
              toast.dismiss(id);
              router.push(href);
            }}
            onDismiss={() => toast.dismiss(id)}
            onAutoClose={() => toast.dismiss(id)}
          />
        ),
        { id: item.id, duration: Infinity },
      );
    },
    [router],
  );
  useToastLedger<PublicStaffToastEntry>(topic, show);

  return null;
}
